import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';

interface PromptSuggestion {
  category: string;
  suggestions: string[];
}

interface PromptHelperProps {
  template: string;
  onSuggestionClick: (suggestion: string) => void;
}

const PromptHelper: React.FC<PromptHelperProps> = ({ template, onSuggestionClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Template-specific suggestions based on Gemini best practices
  const getTemplateSuggestions = (): PromptSuggestion[] => {
    const commonSuggestions = [
      {
        category: 'Camera & Composition',
        suggestions: [
          'wide-angle shot for full product view',
          'close-up macro shot for detail',
          'three-quarter angle view',
          'eye-level perspective',
          'shallow depth of field with bokeh background'
        ]
      },
      {
        category: 'Lighting',
        suggestions: [
          'soft natural window light',
          'professional three-point studio lighting',
          'golden hour warm lighting',
          'high-key bright and airy',
          'dramatic rim lighting'
        ]
      },
      {
        category: 'Style & Mood',
        suggestions: [
          'minimalist Scandinavian aesthetic',
          'luxury premium presentation',
          'cozy lifestyle setting',
          'modern urban environment',
          'eco-friendly natural vibe'
        ]
      }
    ];

    // Template-specific additions
    const templateSpecific: { [key: string]: PromptSuggestion[] } = {
      'tshirt': [
        {
          category: 'Model & Pose',
          suggestions: [
            'confident model with arms crossed',
            'casual relaxed stance',
            'dynamic action pose',
            'sitting casually on stool',
            'walking motion captured mid-stride'
          ]
        }
      ],
      'mug': [
        {
          category: 'Setting & Props',
          suggestions: [
            'on wooden breakfast table with pastries',
            'held in hands with cozy blanket',
            'office desk with laptop nearby',
            'coffee shop table with beans scattered',
            'kitchen counter with steam rising'
          ]
        }
      ],
      'poster': [
        {
          category: 'Interior & Display',
          suggestions: [
            'modern living room above sofa',
            'gallery wall with multiple frames',
            'minimalist bedroom setting',
            'creative studio workspace',
            'professional office environment'
          ]
        }
      ]
    };

    const baseCategory = template.split('-')[0];
    return [
      ...commonSuggestions,
      ...(templateSpecific[baseCategory] || [])
    ];
  };

  const suggestions = getTemplateSuggestions();

  return (
    <div className="bg-gray-50 rounded-lg p-4 mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <Info className="text-blue-500 w-5 h-5" />
          <span className="font-medium text-gray-700">Prompt Writing Tips</span>
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Best Practices */}
          <div className="bg-white rounded p-3 text-sm">
            <h4 className="font-semibold text-gray-800 mb-2">Best Practices (from Gemini docs):</h4>
            <ul className="space-y-1 text-gray-600">
              <li>• Be hyper-specific with descriptions</li>
              <li>• Use photographic terminology (angles, lighting, lenses)</li>
              <li>• Describe what you want, not what you don't want</li>
              <li>• Include context about the purpose or brand</li>
              <li>• Use step-by-step instructions for complex scenes</li>
            </ul>
          </div>

          {/* Suggestion Categories */}
          <div className="space-y-2">
            {suggestions.map((category) => (
              <div key={category.category} className="bg-white rounded overflow-hidden">
                <button
                  onClick={() => setActiveCategory(
                    activeCategory === category.category ? null : category.category
                  )}
                  className="w-full px-3 py-2 text-left font-medium text-gray-700 hover:bg-gray-50 flex justify-between items-center"
                >
                  {category.category}
                  {activeCategory === category.category ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {activeCategory === category.category && (
                  <div className="px-3 py-2 space-y-1">
                    {category.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => onSuggestionClick(suggestion)}
                        className="block w-full text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded px-2 py-1 transition-colors"
                      >
                        + {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Example Prompts */}
          <div className="bg-blue-50 rounded p-3 text-sm">
            <h4 className="font-semibold text-blue-900 mb-2">Example for {template}:</h4>
            <p className="text-blue-800 italic">
              {template.includes('tshirt') && 
                '"Professional fashion photography of model wearing t-shirt, three-quarter angle, soft studio lighting with subtle rim light, minimalist backdrop, confident relaxed pose"'
              }
              {template.includes('mug') && 
                '"Ceramic mug on rustic wooden table, morning window light creating soft shadows, steam rising from fresh coffee, shallow depth of field with lifestyle props"'
              }
              {template.includes('poster') && 
                '"Framed poster in modern Scandinavian living room, eye-level view above minimalist sofa, warm ambient lighting, contemporary interior design context"'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptHelper;
